/**
 * OneTimeInstanceDialog — Add/Edit a discrete GST One-Time Activity instance.
 *
 * Kind-specific fields:
 *   • registration: client (or prospect_name fallback) + gstin (after issuance)
 *                   + category + registration_date
 *   • cancellation: client + gstin + cancellation_date
 *   • amendment   : client + gstin + amendment_type + remarks
 *   • notice      : client + GSTIN (dropdown of client's GSTINs) + notice_no
 *                   + notice_date + due_date
 *
 * On save, calls `tasksAPI.createOneTimeInstance` (add) or
 * `tasksAPI.updateOneTimeInstance` (edit) and surfaces server validation
 * errors (e.g. missing fields for Registration approval) via toast.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Textarea } from '@/components/ui/textarea';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { tasksAPI } from '@/lib/api';
import { toast } from 'sonner';

const KIND_LABELS = {
  registration: 'GST Registration',
  cancellation: 'GST Cancellation',
  amendment:    'GST Amendment',
  notice:       'GST Notice',
};

const GST_CATEGORIES = ['Regular', 'Composition', 'Tax Deductor', 'ISD'];

const blankFor = (kind) => ({
  client_id: '',
  prospect_name: '',
  gstin: '',
  category: 'Regular',
  registration_date: '',
  cancellation_date: '',
  notice_no: '',
  notice_date: '',
  due_date: '',
  amendment_type: '',
  remarks: '',
});

export default function OneTimeInstanceDialog({
  open, onOpenChange, taskId, subtaskId, kind, clients, instance, onSaved,
}) {
  const isEdit = !!instance;
  const [form, setForm] = useState(blankFor(kind));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (instance) {
      setForm({
        client_id: instance.client_id?.startsWith('prospect:') ? '' : (instance.client_id || ''),
        prospect_name: instance.prospect_name || '',
        gstin: instance.gstin || '',
        category: instance.category || 'Regular',
        registration_date: instance.registration_date || '',
        cancellation_date: instance.cancellation_date || '',
        notice_no: instance.notice_no || '',
        notice_date: instance.notice_date || '',
        due_date: instance.due_date || '',
        amendment_type: instance.amendment_type || '',
        remarks: instance.remarks || '',
      });
    } else {
      setForm(blankFor(kind));
    }
  }, [open, instance, kind]);

  // Client picker open state (controls the Popover combobox)
  const [clientPickerOpen, setClientPickerOpen] = useState(false);

  // GSTINs available on the chosen client (for dropdown filtering).
  const selectedClient = useMemo(
    () => clients.find(c => c.id === form.client_id),
    [clients, form.client_id],
  );
  const clientGstins = useMemo(() => {
    const list = selectedClient?.gstins || [];
    return list.map(g => (typeof g === 'string' ? { number: g, category: 'Regular' } : g)).filter(g => g.number);
  }, [selectedClient]);

  const handleSubmit = async () => {
    // Validate kind-specific minimums
    if (kind === 'registration' && !form.client_id && !form.prospect_name.trim()) {
      toast.error('Pick an existing client OR enter the prospect name.');
      return;
    }
    if (kind !== 'registration' && !form.client_id) {
      toast.error('Please select a client.');
      return;
    }
    if (kind === 'notice' && !form.notice_no.trim()) {
      toast.error('Notice number is required.');
      return;
    }

    const payload = { kind };
    if (form.client_id) payload.client_id = form.client_id;
    if (form.prospect_name.trim()) payload.prospect_name = form.prospect_name.trim();
    if (form.gstin.trim()) payload.gstin = form.gstin.trim().toUpperCase();
    if (form.category) payload.category = form.category;
    if (form.registration_date) payload.registration_date = form.registration_date;
    if (form.cancellation_date) payload.cancellation_date = form.cancellation_date;
    if (form.notice_no) payload.notice_no = form.notice_no.trim();
    if (form.notice_date) payload.notice_date = form.notice_date;
    if (form.due_date) payload.due_date = form.due_date;
    if (form.amendment_type) payload.amendment_type = form.amendment_type.trim();
    if (form.remarks) payload.remarks = form.remarks.trim();

    setSaving(true);
    try {
      if (isEdit) {
        await tasksAPI.updateOneTimeInstance(taskId, subtaskId, instance.instance_id, payload);
        toast.success('Instance updated');
      } else {
        await tasksAPI.createOneTimeInstance(taskId, subtaskId, payload);
        toast.success(`${KIND_LABELS[kind]} instance added`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save instance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="one-time-instance-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit' : 'Add'} {KIND_LABELS[kind]}</DialogTitle>
          <DialogDescription className="text-xs">
            {kind === 'notice' && 'Fill notice number, date received, and statutory reply due date.'}
            {kind === 'registration' && 'Pick the client (or enter prospect name for a new lead). Fill the GSTIN once issued.'}
            {kind === 'cancellation' && 'Pick the client and the GSTIN to be cancelled.'}
            {kind === 'amendment' && 'Pick the client and GSTIN; describe the amendment.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {/* CLIENT picker — searchable combobox (Popover + Command). Required
              because firms have 900+ clients; native Select dropdown is unusable. */}
          <div className="grid gap-1.5">
            <Label className="text-xs">Client {kind === 'registration' ? '(optional — if existing)' : '*'}</Label>
            <Popover open={clientPickerOpen} onOpenChange={setClientPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={clientPickerOpen}
                  className="h-9 w-full justify-between text-sm font-normal"
                  data-testid="one-time-client-trigger"
                >
                  {form.client_id
                    ? (clients.find(c => c.id === form.client_id)?.name || 'Selected client')
                    : <span className="text-muted-foreground">Search clients...</span>}
                  <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
                <Command
                  // Custom filter — match against name, company_name, PAN, and any GSTIN.
                  filter={(value, search) => {
                    const c = clients.find(x => x.id === value);
                    if (!c) return 0;
                    const haystack = [
                      c.name || '',
                      c.company_name || '',
                      c.pan || '',
                      ...(c.gstins || []).map(g => (typeof g === 'string' ? g : g.number || '')),
                    ].join(' ').toLowerCase();
                    return haystack.includes(search.toLowerCase()) ? 1 : 0;
                  }}
                >
                  <CommandInput placeholder="Search by name, company, PAN, or GSTIN..." className="h-9 text-sm" data-testid="one-time-client-search" />
                  <CommandList className="max-h-64">
                    <CommandEmpty>No clients found.</CommandEmpty>
                    <CommandGroup>
                      {kind === 'registration' && (
                        <CommandItem
                          value="__new_prospect__"
                          onSelect={() => {
                            setForm(p => ({ ...p, client_id: '', gstin: '' }));
                            setClientPickerOpen(false);
                          }}
                          className="text-xs italic text-muted-foreground"
                        >
                          — New prospect (enter name below) —
                        </CommandItem>
                      )}
                      {clients.map(c => (
                        <CommandItem
                          key={c.id}
                          value={c.id}
                          onSelect={() => {
                            setForm(p => ({ ...p, client_id: c.id, gstin: '' }));
                            setClientPickerOpen(false);
                          }}
                          className="text-sm"
                        >
                          <Check className={cn('mr-2 h-3.5 w-3.5', form.client_id === c.id ? 'opacity-100' : 'opacity-0')} />
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="truncate">{c.name}</span>
                            {(c.pan || (c.gstins || []).length > 0) && (
                              <span className="text-[10px] text-muted-foreground truncate">
                                {c.pan && `PAN: ${c.pan}`}
                                {c.pan && (c.gstins || []).length > 0 && ' · '}
                                {(c.gstins || []).length > 0 && `${(c.gstins || []).length} GSTIN${(c.gstins || []).length === 1 ? '' : 's'}`}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {kind === 'registration' && !form.client_id && (
            <div className="grid gap-1.5">
              <Label className="text-xs">Prospect Name *</Label>
              <Input className="h-9 text-sm" value={form.prospect_name}
                onChange={e => setForm(p => ({ ...p, prospect_name: e.target.value }))}
                placeholder="e.g. Acme Industries Pvt Ltd"
                data-testid="one-time-prospect-name" />
            </div>
          )}

          {/* GSTIN — pick from client's existing GSTINs OR free-input (for new registrations) */}
          {kind !== 'registration' && (
            <div className="grid gap-1.5">
              <Label className="text-xs">GSTIN *</Label>
              <Select value={form.gstin || '_none'} onValueChange={v => setForm(p => ({ ...p, gstin: v === '_none' ? '' : v }))}>
                <SelectTrigger className="h-9 text-sm" data-testid="one-time-gstin-trigger">
                  <SelectValue placeholder={form.client_id ? 'Select GSTIN...' : 'Select client first'} />
                </SelectTrigger>
                <SelectContent>
                  {clientGstins.length === 0 ? (
                    <SelectItem value="_none" disabled><span className="text-xs text-muted-foreground">No GSTINs on this client</span></SelectItem>
                  ) : clientGstins.map(g => (
                    <SelectItem key={g.number} value={g.number}><span className="text-xs">{g.number} — {g.category}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {kind === 'registration' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label className="text-xs">GSTIN (after issuance)</Label>
                  <Input className="h-9 text-sm font-mono" value={form.gstin}
                    onChange={e => setForm(p => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                    placeholder="29ABCDE1234F1Z5" maxLength={15}
                    data-testid="one-time-gstin-input" />
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-xs">Category</Label>
                  <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                    <SelectTrigger className="h-9 text-sm" data-testid="one-time-category-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GST_CATEGORIES.map(c => <SelectItem key={c} value={c}><span className="text-xs">{c}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">Registration Date</Label>
                <Input type="date" className="h-9 text-sm" value={form.registration_date}
                  onChange={e => setForm(p => ({ ...p, registration_date: e.target.value }))}
                  data-testid="one-time-registration-date" />
              </div>
            </>
          )}

          {kind === 'cancellation' && (
            <div className="grid gap-1.5">
              <Label className="text-xs">Effective Cancellation Date</Label>
              <Input type="date" className="h-9 text-sm" value={form.cancellation_date}
                onChange={e => setForm(p => ({ ...p, cancellation_date: e.target.value }))}
                data-testid="one-time-cancellation-date" />
            </div>
          )}

          {kind === 'amendment' && (
            <div className="grid gap-1.5">
              <Label className="text-xs">Amendment Type / Description</Label>
              <Input className="h-9 text-sm" value={form.amendment_type}
                onChange={e => setForm(p => ({ ...p, amendment_type: e.target.value }))}
                placeholder="e.g. Address change, Authorized Signatory update"
                data-testid="one-time-amendment-type" />
            </div>
          )}

          {kind === 'notice' && (
            <>
              <div className="grid gap-1.5">
                <Label className="text-xs">Notice No. *</Label>
                <Input className="h-9 text-sm font-mono" value={form.notice_no}
                  onChange={e => setForm(p => ({ ...p, notice_no: e.target.value }))}
                  placeholder="e.g. ZA290823001234A"
                  data-testid="one-time-notice-no" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5">
                  <Label className="text-xs">Notice Date</Label>
                  <Input type="date" className="h-9 text-sm" value={form.notice_date}
                    onChange={e => setForm(p => ({ ...p, notice_date: e.target.value }))}
                    data-testid="one-time-notice-date" />
                </div>
                <div className="grid gap-1.5">
                  <Label className="text-xs">Reply Due Date</Label>
                  <Input type="date" className="h-9 text-sm" value={form.due_date}
                    onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                    data-testid="one-time-due-date" />
                </div>
              </div>
            </>
          )}

          <div className="grid gap-1.5">
            <Label className="text-xs">Remarks</Label>
            <Textarea className="text-sm min-h-[60px]" value={form.remarks}
              onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))}
              placeholder="Optional notes..."
              data-testid="one-time-remarks" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-one-time-instance">
            {saving ? 'Saving...' : (isEdit ? 'Save changes' : 'Add instance')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
